import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import cx from 'classnames/bind';
import Button from '../Button/Button';

/**
 * A component that displays events on a vertical calendar.
 * Before the component gets mounted it will first find the first day of the week,
 * based on the supplied start moment, and calculate the interval displayed. Then
 * it will trigger an onChange event telling the interval data should be supplied for
 *
 * Also exports the function that is used to calculate vertical offset / height of each
 * event as well as the default DEFAULT_ZOOM_LEVEL constant
 *
 * @property {array} events Array of events that will be displayed on the calendar
 * @property {function} getEventInterval Function to get the start and end moments on the
 * form { start: ..., end: ... }
 * @property {function} eventRenderer Function that will be used to produce the DOM for
 * a single event, accepts React Component Classes or pure functions that return jsx
 * @property {number} zoomLevel Number used to calculate how much space each minute on the
 * calendar should use. eg. 10 is the default
 * @property {object} start A moment that declares any given day you wish to view.
 * The calendar will automatically find the start and end of the week for that given
 * moment and immediately fire a onChange once the interval has been calculated
 * @property {function} onChange Handle for when the displayed interval has been changed
 *
 * @extends React Component
 * @see WeekCalendar
 */

export const DEFAULT_ZOOM_LEVEL = 7;

export const getDays = (start) => getWeekDays(getStartOfWeek(start));

export const generateMarkers = (start) => {
  const numberOfMarkers = 24 * 2; // one for each 30 minutes
  const markerStart = getStartOfWeek(start);

  const markerArr = new Array(numberOfMarkers);
  let idx = 0;
  while (idx < numberOfMarkers) {
    const nextStart = markerStart.clone().add(idx * 30, 'minutes');
    const nextEnd = nextStart.clone().add(30, 'minutes');
    markerArr[idx] = { start: nextStart, end: nextEnd };
    idx += 1;
  }

  return markerArr;
};

const getStartOfWeek = function(day) {
  const startOfWeek = day.clone();
  startOfWeek.day(0);
  startOfWeek.startOf('day');
  startOfWeek.milliseconds(0);
  startOfWeek.seconds(0);
  startOfWeek.minutes(0);
  startOfWeek.hours(0);
  return startOfWeek;
};

const getStartOfDay = function(day) {
  const startOfDay = day.clone();
  startOfDay.startOf('day');
  startOfDay.milliseconds(0);
  startOfDay.seconds(0);
  startOfDay.minutes(0);
  startOfDay.hours(0);
  return startOfDay;
};

const getEndOfDay = function(day) {
  const endOfDay = day.clone();
  endOfDay.startOf('day');
  endOfDay.milliseconds(999);
  endOfDay.seconds(59);
  endOfDay.minutes(59);
  endOfDay.hours(23);
  return endOfDay;
};

const getWeekDays = function(start) {
  const numberOfDays = 7; // one for each 30 minutes
  const daysStart = getStartOfDay(start).clone();
  const daysArr = new Array(numberOfDays);
  let idx = 0;
  while (idx < numberOfDays) {
    const next = daysStart.clone();
    daysStart.add(1, 'days');
    daysArr[idx] = next;
    idx += 1;
  }

  return daysArr;
};

export default class WeekCalendar extends Component {

  static displayName = 'WeekCalendar';

  static getOffset = function(start, end, day, zoomLevel = DEFAULT_ZOOM_LEVEL) {
    const startOfDay = getStartOfDay(day);
    const endOfDay = getEndOfDay(day);
    let minEnd;
    if (end.isBefore(startOfDay)) {
      minEnd = startOfDay;
    } else if (end.isBefore(endOfDay)) {
      minEnd = end;
    } else {
      minEnd = endOfDay;
    }

    const minStart = start.isBefore(startOfDay) ? startOfDay : start;

    const offset = minEnd.diff(minStart, 'minutes');
    return zoomLevel * (offset / 3);
  };

  static propTypes = {
    events: PropTypes.array.isRequired, // eslint-disable-line
    getEventInterval: PropTypes.func.isRequired, // eslint-disable-line
    eventRenderer: PropTypes.func.isRequired, // eslint-disable-line
    zoomLevel: PropTypes.number.isRequired,
    start: PropTypes.object.isRequired,
    className: PropTypes.string,
    onChange: PropTypes.func.isRequired
  };

  /* eslint-disable react/default-props-match-prop-types */
  static defaultProps = {
    getEventInterval: (event) => (
      { start: event.start, end: event.end }
    ),
    eventRenderer: ({ event }) => (
      <div>{event.start.format('HH:mm')} - {event.end.format('HH:mm')}</div>
    ),
    /**
     * @todo onChange functionality needs to be implemented 
     */
    onChange: (event) => {
      
    },
    zoomLevel: DEFAULT_ZOOM_LEVEL,
    start: moment(),
    events: []
  };
  /* eslint-enable react/default-props-match-prop-types */

  constructor (props) {
    super(props);
    this.state = {
      now: moment(),
      start: getStartOfWeek(props.start).clone()
    };
  }

  componentDidMount () {
    const { onChange, start } = this.props;
    const newStart = getStartOfWeek(start).clone();
    const newEnd = getStartOfWeek(newStart).clone().add(1, 'week').subtract(1, 'day');
    onChange({ start: newStart, end: newEnd });

    this.autoScroll();
  }

  componentDidUpdate (prevProps, prevState) {
    if (!prevProps.start.isSame(this.props.start)) {
      this.setState({ start: getStartOfWeek(this.props.start).clone() });
    }

    if (!prevProps.start.isSame(this.props.start) || !prevState.start.isSame(this.state.start)) {
      this.autoScroll();
    }
  }

  componentWillUnmount () {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  autoScroll = () => {
    const { zoomLevel } = this.props;
    const { start } = this.state;
    const { now } = this.state;
    const intervalStart = getStartOfWeek(start).clone();
    const intervalEnd = getStartOfWeek(intervalStart).clone().add(1, 'week').subtract(1, 'day');

    if (this.state.now.isBetween(intervalStart, intervalEnd)) {
      const todayStart = getStartOfDay(now);
      const visbleHeight = this.scrollDiv.offsetHeight;
      const contentHeight = this.contentDiv.offsetHeight;
      const overflow = contentHeight - visbleHeight;
      const nowOffset = WeekCalendar.getOffset(todayStart, now, now, zoomLevel);
      if (nowOffset > visbleHeight && overflow > 0) {
        this.scrollDiv.scrollTop = (nowOffset);
      }
      // we probably want to scroll to now
    }
  }

  startTimer = () => {
    this.intervalId = setInterval(() => {
      this.setState({ now: moment() });
    }, 6000);
  }

  handleOnNextWeek = () => {
    const { onChange } = this.props;
    const { start } = this.state;
    const newStart = getStartOfWeek(start).clone().add(1, 'week');
    const newEnd = getStartOfWeek(newStart).clone().add(1, 'week').subtract(1, 'day');
    onChange({ start: newStart, end: newEnd });
    this.setState({ start: newStart });
  }

  handleOnPrevWeek = () => {
    const { onChange } = this.props;
    const { start } = this.state;
    const newStart = getStartOfWeek(start).clone().subtract(1, 'week');
    const newEnd = getStartOfWeek(newStart).clone().add(1, 'week').subtract(1, 'day');
    onChange({ start: newStart, end: newEnd });
    this.setState({ start: newStart });
  }

  render () {
    const { now } = this.state;
    const { className } = this.props;
    const { start } = this.state;
    const classes = cx('core-week-calendar', className);
    return (
      <div className={classes}>
        <div className="core-week-calendar-header">
          <Button iconName="arrow-left" onClick={this.handleOnPrevWeek}/>
          <div className="core-week-calendar-header-now">{now.format('llll')}</div>
          <Button iconName="arrow-right" onClick={this.handleOnNextWeek}/>
        </div>
        <CalendarWeekHeader {...this.props} start={start} />
        <div className="core-week-calendar-main" ref={(c) => { this.scrollDiv = c; }}>
          <div className="core-week-calendar-main-inner" ref={(c) => { this.contentDiv = c; }}>
            <Sidebar {...this.props} start={start}/>
            <div className="core-week-calendar-content">
              <CalendarDays {...this.props} start={start}/>
            </div>
          </div>
        </div>
      </div>
    );
  }

}

class Sidebar extends Component {

  static propTypes = {
    zoomLevel: PropTypes.number.isRequired,
    start: PropTypes.object.isRequired
  };

  render () {
    const { zoomLevel, start } = this.props;
    const markers = generateMarkers(start);

    return (
      <div className="core-week-calendar-sidebar">
        <div className="core-week-calendar-sidebar-times">
          {markers.map((marker, idx) => {
            const style = {
              height: WeekCalendar.getOffset(marker.start, marker.end, marker.start, zoomLevel)
            };
            return (
              <div key={idx} style={style} className="core-week-calendar-sidebar-time" data-testid="time-slot">
                {marker.start.format('HH:mm')}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

}

class CalendarDays extends Component {

  static propTypes = {
    eventRenderer: PropTypes.func.isRequired, // eslint-disable-line
    events: PropTypes.array.isRequired, // eslint-disable-line
    start: PropTypes.object.isRequired,
    zoomLevel: PropTypes.number.isRequired, // eslint-disable-line
    getEventInterval: PropTypes.func.isRequired // eslint-disable-line
  };

  getEventsForDay (day) {
    const { events, getEventInterval } = this.props;
    const arr = [];
    events.forEach((event) => {
      if (getEventInterval(event).start.isSame(day, 'day') || getEventInterval(event).end.isSame(day, 'day')) {
        arr.push(event);
      }
    });
    return arr;
  }

  render () {
    const { start } = this.props;
    const days = getDays(start);

    return (
      <div className="core-week-calendar-days">
        {
          days.map((day, idx) => {
            const events = this.getEventsForDay(day);
            return (
              <CalendarDay {...this.props} key={idx} day={day} events={events}/>
            );
          })
        }
      </div>
    );
  }

}

class CalendarWeekHeader extends Component {

  static propTypes = {
    start: PropTypes.object.isRequired
  };

  render () {
    const { start } = this.props;
    const days = getWeekDays(getStartOfWeek(start));
    return (
      <div className="core-week-calendar-week-header">
        <div className="core-week-calendar-day-header">
          <div className="core-week-calendar-day-header-weekday" data-testid="calendar-header">
            GMT {moment().format('ZZ')}
          </div>
          <div className="core-week-calendar-day-header-date"/>
        </div>
        {
          days.map((day, idx) => (
            <div key={idx} className="core-week-calendar-day-header">
              <div className="core-week-calendar-day-header-weekday" data-testid="calendar-header">
                {day.format('ddd')}
              </div>
              <div className="core-week-calendar-day-header-date" data-testid="date-header">
                {day.format('Do')}
              </div>
            </div>
          ))
        }
      </div>
    );
  }

}


class CalendarDay extends Component {

  static propTypes = {
    eventRenderer: PropTypes.func.isRequired,
    events: PropTypes.array.isRequired,
    zoomLevel: PropTypes.number,
    getEventInterval: PropTypes.func.isRequired,
    day: PropTypes.object.isRequired
  };

  render () {
    const { events, zoomLevel, getEventInterval, day } = this.props;

    const startOfDay = getStartOfDay(day);

    const Event = this.props.eventRenderer;

    return (
      <div className="core-week-calendar-day">
        <div className="core-week-calendar-day-data">
          {
            events.map((event, idx) => {

              const { start, end } = getEventInterval(event);

              const style = {
                height: WeekCalendar.getOffset(start, end, day, zoomLevel),
                top: WeekCalendar.getOffset(startOfDay, start, day, zoomLevel)
              };

              return (
                <div key={idx} style={style} className="core-week-calendar-event" data-testid="calendar-event">
                  <Event event={event} day={day}/>
                </div>
              );
            })
          }
        </div>
      </div>
    );
  }

}
